import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL, USER_URL } from "../../constant/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCloudUploadAlt } from "react-icons/fa";
import { ScaleLoader } from "react-spinners"; // Import ScaleLoader

function EditCategory() {
  const { catId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState({ name: "", image: "" });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch category data
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const response = await USER_URL.get(`/categories/admin/${catId}/`);
        const categoryData = response.data.Data[0];
        
        setCategory({
          name: categoryData.name,
          image: categoryData.image
        });
        
        // Set preview for existing image if it exists
        if (categoryData.image) {
          setPreview(
            categoryData.image.startsWith("http") 
              ? categoryData.image 
              : `${BASE_URL}${categoryData.image}`
          );
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching category:", error);
        toast.error("Failed to load category data");
        navigate("/admin/categories");
      }
    };
    fetchCategory();
  }, [catId, navigate]);

  // Handle Image Upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  // Handle Name Change
  const handleNameChange = (e) => {
    setCategory(prev => ({
      ...prev,
      name: e.target.value
    }));
  };

  // Form Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!category.name) {
      toast.warn("Category name is required");
      return;
    }

    const formData = new FormData();
    formData.append("name", category.name);
    if (image) {
      formData.append("image", image);
    }

    try {
      setIsSubmitting(true);
      
      const response = await USER_URL.put(
        `/categories/admin/${catId}/`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.success("Category updated successfully!");
      console.log("Response:", response.data);
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error(error.response?.data?.message || "Failed to update category.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <ScaleLoader color="#FFAE00" size={40} />
      </div>
    );
  }

  return (
    <div className="p-3">
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="border-2 rounded-lg shadow-lg bg-white p-16 pt-8 w-full">
        <h2 className="text-green-600 text-3xl font-bold text-center mb-8 p-5">Edit Category</h2>

        <form onSubmit={handleSubmit} className="flex items-start justify-between">
          {/* Image Upload Section */}
          <div className="flex flex-col w-1/2">
            <label className="text-md font-medium mb-2">Category Image</label>
            <div className="w-44 h-44 border flex items-center justify-center rounded-lg overflow-hidden bg-gray-100">
              {preview ? (
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-center text-gray-500 text-sm">No image selected</span>
              )}
            </div>
           
            {/* Upload Button */}
            <label className="flex items-center cursor-pointer text-white bg-blue-500 px-4 py-2 mt-3 w-44 rounded-md hover:bg-blue-600 text-sm">
              <FaCloudUploadAlt className="mr-2 text-lg" />
              {preview ? "Change Image" : "Upload Image"}
              <input 
                type="file" 
                onChange={handleImageChange} 
                className="hidden" 
                accept="image/*" 
              />
            </label>
            {category.image && !preview && (
              <p className="text-sm text-gray-500 mt-1">Current image will be kept</p>
            )}
          </div>

          {/* Category Name Input */}
          <div className="flex flex-col w-1/2">
            <label className="text-md font-medium mb-2">Category Name</label>
            <input
              type="text"
              value={category.name}
              onChange={handleNameChange}
              placeholder="Enter Category name"
              className="border mb-5 px-3 py-2 rounded focus:outline-none focus:ring focus:ring-green-200"
              required
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-green-600 text-white py-2 rounded mt-4 transition ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:bg-green-700"
              }`}
            >
              {isSubmitting ? "Updating..." : "Update Category"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditCategory;
